"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowLeft, Clock, ExternalLink } from "@/components/icons";
import { AuthorAvatar, HeroGlow } from "@/components/ui";
import CodeBlock from "@/components/ui/code-block";
import FaqSection from "@/components/sections/products/shared/faq";

type RichTextSpan = { type: string; start: number; end: number; data?: { url?: string } };
type RichTextNode = { type: string; text: string; spans: RichTextSpan[]; direction?: string };
type ContentBlock = {
  block_type: string | null;
  body_content: RichTextNode[];
  image: { url?: string; alt?: string } | Record<string, never>;
  caption: string | null;
  code_language: string | null;
};
type HeroStat = {
  value: string | null;
  label: string | null;
  description: string | null;
  source_label: string | null;
  source_url: { url?: string } | null;
};
type FAQ = { question: string | null; answer: RichTextNode[] };

interface Props {
  title: string;
  heroDescription: RichTextNode[];
  ctaLabel: string | null;
  ctaHref: string | null;
  heroStats: HeroStat[];
  featuredImage: string | null;
  contentBlocks: ContentBlock[];
  faqs: FAQ[];
  authorName: string | null;
  authorImage: string | null;
  authorUid: string | null;
}

function escapeHtml(str: string) {
  return (str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function applySpans(text: string, spans: RichTextSpan[]): string {
  const safeText = text ?? "";
  if (!spans?.length) return escapeHtml(safeText);
  const sorted = [...spans].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start)
  );
  const processed = new Set<number>();
  let result = "";
  let cursor = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (processed.has(i)) continue;
    const span = sorted[i];
    if (span.start < cursor) {
      processed.add(i);
      continue;
    }
    result += escapeHtml(safeText.slice(cursor, span.start));
    const nestedSpans: RichTextSpan[] = [];
    for (let j = i + 1; j < sorted.length; j++) {
      const s = sorted[j];
      if (s.start >= span.start && s.end <= span.end) {
        nestedSpans.push({ ...s, start: s.start - span.start, end: s.end - span.start });
        processed.add(j);
      }
    }
    const innerText = safeText.slice(span.start, span.end);
    const inner = nestedSpans.length ? applySpans(innerText, nestedSpans) : escapeHtml(innerText);
    if (span.type === "strong") result += `<strong>${inner}</strong>`;
    else if (span.type === "em") result += `<em>${inner}</em>`;
    else if (span.type === "hyperlink" && span.data?.url) {
      const isExternal = !span.data.url.includes("proxidize.com") && !span.data.url.startsWith("/");
      const relParts = ["noopener", ...(isExternal ? ["noreferrer", "nofollow"] : [])];
      const targetAttr = isExternal ? ` target="_blank"` : "";
      result += `<a href="${span.data.url}" class="text-foreground underline underline-offset-2 hover:opacity-80"${targetAttr} rel="${relParts.join(" ")}">${inner}</a>`;
    } else result += inner;
    cursor = span.end;
  }
  result += escapeHtml(safeText.slice(cursor));
  return result;
}

type RichGroup =
  | { type: "ul"; items: RichTextNode[] }
  | { type: "ol"; items: RichTextNode[] }
  | { type: "node"; node: RichTextNode };

function groupNodes(nodes: RichTextNode[]): RichGroup[] {
  const groups: RichGroup[] = [];
  for (const node of nodes) {
    const last = groups[groups.length - 1];
    if (node.type === "list-item") {
      if (last?.type === "ul") last.items.push(node);
      else groups.push({ type: "ul", items: [node] });
    } else if (node.type === "o-list-item") {
      if (last?.type === "ol") last.items.push(node);
      else groups.push({ type: "ol", items: [node] });
    } else {
      groups.push({ type: "node", node });
    }
  }
  return groups;
}

function isPipeTable(text: string): boolean {
  const lines = text
    .trim()
    .split("\n")
    .filter((l) => l.trim());
  if (lines.length < 2) return false;
  return lines.filter((l) => l.includes("|")).length >= Math.ceil(lines.length * 0.8);
}

function parseRow(line: string): string[] {
  const parts = line.split("|").map((c) => c.trim());
  if (parts[0] === "") parts.shift();
  if (parts[parts.length - 1] === "") parts.pop();
  return parts;
}

const TABLE_INLINE_PATTERN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

/** Preformatted table cells are raw pasted text, not Prismic rich-text spans, so
 *  `**bold**` and `[label](url)` markers never go through `applySpans` — parse
 *  them here instead. */
function renderInlineMarkdown(text: string): React.ReactNode {
  if (!text.includes("**") && !text.includes("](")) return text;
  const parts = text.split(TABLE_INLINE_PATTERN).filter(Boolean);
  let offset = 0;
  return parts.map((part) => {
    const start = offset;
    offset += part.length;

    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={start} className="text-foreground font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = !href.includes("proxidize.com") && !href.startsWith("/");
      const relParts = ["noopener", ...(isExternal ? ["noreferrer", "nofollow"] : [])];
      return (
        <a
          key={start}
          href={href}
          className="text-foreground underline underline-offset-2 hover:opacity-80"
          target={isExternal ? "_blank" : undefined}
          rel={relParts.join(" ")}
        >
          {label}
        </a>
      );
    }

    return <span key={start}>{part}</span>;
  });
}

function ProseTable({ text }: { text: string }) {
  const lines = text
    .trim()
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^[\s|:-]+$/.test(l));
  const [headerLine, ...dataLines] = lines;
  const headers = parseRow(headerLine);
  const rows = dataLines.map(parseRow);
  return (
    <div className="border-border my-6 overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border bg-card border-b">
            {headers.map((h) => (
              <th
                key={h}
                className="text-foreground px-4 py-3 text-left text-xs font-semibold tracking-wide whitespace-nowrap uppercase"
              >
                {renderInlineMarkdown(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&>tr:nth-child(even)]:bg-surface-2">
          {rows.map((row) => (
            <tr key={row.join("|")} className="border-border/50 border-b last:border-0">
              {row.map((cell) => (
                <td key={cell} className="text-muted-foreground px-4 py-3 leading-relaxed">
                  {renderInlineMarkdown(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RichText({ nodes, codeLang }: { nodes: RichTextNode[]; codeLang?: string | null }) {
  if (!nodes?.length) return null;
  const groups = groupNodes(nodes);
  return (
    <div className="space-y-4">
      {groups.map((group) => {
        if (group.type === "ul") {
          return (
            <ul
              key={`ul-${group.items[0]?.text?.slice(0, 30) ?? "empty"}`}
              className="list-outside list-disc space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const html = applySpans(node.text ?? "", node.spans);
                return (
                  <li
                    key={`li-${node.text?.slice(0, 40)}`}
                    dir={node.direction === "rtl" ? "rtl" : undefined}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ul>
          );
        }
        if (group.type === "ol") {
          return (
            <ol
              key={`ol-${group.items[0]?.text?.slice(0, 30) ?? "empty"}`}
              className="list-outside list-decimal space-y-1 pl-5"
            >
              {group.items.map((node) => {
                const html = applySpans(node.text ?? "", node.spans);
                return (
                  <li
                    key={`li-${node.text?.slice(0, 40)}`}
                    dir={node.direction === "rtl" ? "rtl" : undefined}
                    className="text-muted-foreground 3xl:text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                );
              })}
            </ol>
          );
        }
        const { node } = group;
        const html = applySpans(node.text ?? "", node.spans);
        const dir = node.direction === "rtl" ? "rtl" : undefined;
        const nodeKey = `${node.type}-${(node.text ?? "").slice(0, 40)}`;
        switch (node.type) {
          case "heading1":
            return (
              <h1
                key={nodeKey}
                dir={dir}
                className="3xl:text-4xl text-foreground mt-10 mb-4 scroll-mt-24 text-3xl font-black"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading2":
            return (
              <h2
                key={nodeKey}
                dir={dir}
                className="3xl:text-3xl text-foreground mt-10 mb-4 scroll-mt-24 text-2xl font-bold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading3":
            return (
              <h3
                key={nodeKey}
                dir={dir}
                className="3xl:text-2xl text-foreground mt-8 mb-3 scroll-mt-24 text-xl font-bold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "heading4":
          case "heading5":
          case "heading6":
            return (
              <h4
                key={nodeKey}
                dir={dir}
                className="3xl:text-xl text-foreground mt-6 mb-2 text-lg font-semibold"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          case "preformatted":
            if (isPipeTable(node.text ?? ""))
              return <ProseTable key={nodeKey} text={node.text ?? ""} />;
            return <CodeBlock key={nodeKey} code={node.text ?? ""} lang={codeLang ?? undefined} />;
          default:
            return node.text ? (
              <p
                key={nodeKey}
                dir={dir}
                className="text-muted-foreground 3xl:text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : null;
        }
      })}
    </div>
  );
}

export function PrismicUseCasePost({
  title,
  heroDescription,
  ctaLabel,
  ctaHref,
  heroStats,
  featuredImage: _featuredImage,
  contentBlocks,
  faqs,
  authorName,
  authorImage,
  authorUid,
}: Props) {
  const prefersReducedMotion = useReducedMotion();

  const wordCount = (contentBlocks ?? [])
    .flatMap((b) => b.body_content ?? [])
    .reduce((sum, n) => sum + (n.text ?? "").trim().split(/\s+/).filter(Boolean).length, 0);
  const readMins = Math.max(1, Math.round(wordCount / 200));

  const displayAuthor = authorName ?? "Proxidize";

  return (
    <main id="main-content" tabIndex={-1} className="relative isolate overflow-y-clip">
      {/* The page-hero ribbon. These article templates have no hero section, so
          HeroGlow bounds itself to a band at the top of the document and lands
          where it does on every other page. The main must NOT clip the X axis —
          the ribbon is 100vw and has to bleed past the shell's max-w-7xl frame. */}
      <HeroGlow align="left" behind />
      {/* Hero */}
      <section className="relative overflow-y-clip pt-24 pb-16 sm:pt-28 sm:pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              href="/use-cases"
              className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1.5 text-sm transition-colors"
            >
              <ArrowLeft size={14} />
              All Proxy Use Cases
            </Link>

            <p className="light:text-blue-600 text-brand-yellow mb-4 text-xs font-semibold tracking-widest uppercase">
              Use Case
            </p>

            {/* Meta row — read time + author above title */}
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <Clock size={11} aria-hidden="true" />
                {readMins} min read
              </span>
              <span className="text-muted-foreground/40 text-xs" aria-hidden="true">
                ·
              </span>
              <div className="flex items-center gap-2">
                {authorImage ? (
                  <Image
                    src={authorImage}
                    alt={displayAuthor}
                    width={20}
                    height={20}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                ) : (
                  <AuthorAvatar initial={displayAuthor[0]?.toUpperCase() ?? "P"} color="#5b8cff" />
                )}
                {authorUid ? (
                  <Link
                    href={`/author/${authorUid}`}
                    className="text-foreground text-xs font-semibold underline-offset-2 hover:underline"
                  >
                    {displayAuthor}
                  </Link>
                ) : (
                  <span className="text-foreground text-xs font-semibold">{displayAuthor}</span>
                )}
              </div>
            </div>

            <h1 className="text-foreground mb-6 text-4xl leading-tight font-black tracking-tight sm:text-5xl lg:text-6xl">
              {title}
            </h1>

            {heroDescription.length > 0 && (
              <div className="text-muted-foreground mb-8 max-w-3xl space-y-3 text-lg leading-relaxed">
                <RichText nodes={heroDescription} />
              </div>
            )}

            {ctaHref && ctaLabel && (
              <span className="cta-glow-btn-wrap">
                <Link href={ctaHref} className="cta-glow-btn-inner">
                  {ctaLabel}
                </Link>
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Hero stats */}
      {heroStats.length > 0 && (
        <section className="border-foreground/6 border-y py-10">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4"
            >
              {heroStats.map((stat) => (
                <div
                  key={`${stat.value ?? ""}-${stat.label ?? ""}`}
                  className="border-foreground/6 bg-foreground/2 flex flex-col rounded-2xl border p-5"
                >
                  <p className="text-foreground mb-1 text-3xl font-black tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-foreground mb-2 text-sm font-semibold">{stat.label}</p>
                  {stat.description && (
                    <p className="text-muted-foreground mb-3 text-xs leading-relaxed">
                      {stat.description}
                    </p>
                  )}
                  {stat.source_label && stat.source_url?.url && (
                    <a
                      href={stat.source_url.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-muted-foreground hover:text-foreground mt-auto inline-flex items-center gap-1 text-xs transition-colors"
                    >
                      {stat.source_label}
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Content */}
      <motion.article
        initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="3xl:max-w-5xl mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="space-y-8">
          {contentBlocks.map((block, i) => {
            const blockType = block.block_type ?? "text";
            const hasText = block.body_content?.some((n) => n.text?.trim());
            const imgUrl = (block.image as { url?: string })?.url;
            const blockKey =
              block.body_content?.[0]?.text?.slice(0, 30) ?? (imgUrl ? `img-${i}` : `block-${i}`);

            if (blockType === "image" && imgUrl) {
              return (
                <figure key={blockKey} className="my-6">
                  <div className="border-foreground/6 overflow-hidden rounded-xl border">
                    <Image
                      src={imgUrl}
                      alt={(block.image as { alt?: string }).alt ?? block.caption ?? ""}
                      width={1200}
                      height={630}
                      className="h-auto w-full object-cover"
                    />
                  </div>
                  {block.caption && (
                    <figcaption className="text-muted-foreground mt-2 text-center text-xs">
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            }

            if (blockType === "code" && hasText) {
              const codeNode = block.body_content.find((n) => n.type === "preformatted");
              return codeNode ? (
                <CodeBlock
                  key={blockKey}
                  code={codeNode.text}
                  lang={block.code_language ?? undefined}
                />
              ) : null;
            }

            return (
              <div key={blockKey}>
                {hasText && <RichText nodes={block.body_content} codeLang={block.code_language} />}
                {blockType !== "code" && imgUrl && (
                  <figure className="my-6">
                    <div className="border-foreground/6 overflow-hidden rounded-xl border">
                      <Image
                        src={imgUrl}
                        alt={(block.image as { alt?: string }).alt ?? block.caption ?? ""}
                        width={1200}
                        height={630}
                        className="h-auto w-full object-cover"
                      />
                    </div>
                    {block.caption && (
                      <figcaption className="text-muted-foreground mt-2 text-center text-xs">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>
            );
          })}
        </div>
      </motion.article>

      {faqs.length > 0 && (
        <FaqSection
          faqs={faqs.map((faq) => ({
            q: faq.question ?? "",
            a: <RichText nodes={faq.answer} />,
          }))}
          description="Common questions about web scraping with proxies."
          hideContactLink
        />
      )}
    </main>
  );
}
